import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField } from "@mui/material";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import useModal from "../../hooks/useModal";
import Button from "../ui/Button";
import { updateActualLastWorkingDay } from "../../services/exitManagement";

const remarksSchema = z.object({
  remarks: z.string().min(1, { message: "Remarks are required" }),
  date: z
    .preprocess(
      (val) => (val instanceof Date && !isNaN(val) ? val : null),
      z.union([z.date(), z.null()])
    )
    .refine((val) => val !== null, {
      message: "Date is required",
    })
    // .refine((val) => val instanceof Date && val > new Date(), {
    //   message: "Date must be in the future",
    // }),
});

const EditActualLastWorkingDay = ({ resignationId, fetchResignations }) => {
  const { hideModal } = useModal();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(remarksSchema),
    defaultValues: {
      remarks: "",
      date: null,
    },
  });

  const handleSubmitForm = async (data, resignationId) => {
    try {
      // console.log("Submitting data:", data);
      // return;
      const response = await updateActualLastWorkingDay(resignationId, data);
      if (response.status === 200) {
        toast.success(
          response.data.message || "Resignation processed successfully"
        );
        reset();
        hideModal();
        fetchResignations();
      } else {
        toast.error(response.data.message || "Failed to process resignation");
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast.error(error.response.data.message || "Bad Request: Invalid data");
      } else {
        console.error("Error submitting resignation:", error);
        toast.error("An unexpected error occurred.");
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit((data) => handleSubmitForm(data, resignationId))}
      className="pt-2"
    >
      <div className="row mb-3">
        <div className="col-md-12">
          <Controller
            name="date"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                label="Choose Date*"
                disablePast
                format="dd MMM yyyy"
                disabled={isSubmitting}
                minDate={new Date()}
                slotProps={{
                  textField: {
                    variant: "standard",
                    fullWidth: true,
                    error: !!errors.date,
                    helperText: errors.date?.message,
                  },
                }}
              />
            )}
          />
        </div>
        <div className="col-md-12 mt-2">
          <Controller
            name="remarks"
            control={control}
            render={({ field }) => (
              <TextField
                label="Remarks*"
                variant="standard"
                {...field}
                multiline
                rows={4}
                error={!!errors.remarks}
                helperText={errors.remarks?.message}
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
        </div>
      </div>
      <div className="d-flex justify-content-end gap-1">
        <Button
          variant="success"
          size="small"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
        <Button
          variant="light"
          size="small"
          onClick={hideModal}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default EditActualLastWorkingDay;
